import React from 'react';
import { Button, InputGroup, FormControl, Card, Badge, Spinner, Form, Alert } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import { flaskServiceAPI } from '../../services/apiService';
import { getTranslatedError } from '../../utils/errorTranslator';
import { revokeViewBlobUrl } from '../../utils/mhtmlView';
import { updateTask } from '../../store/taskSlice';
import MhtmlUploadPanel from './MhtmlUploadPanel';
import { DownloadOutlined, EyeOutlined, CopyOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import StackedModal from '../common/StackedModal';

const Modal = StackedModal;

const TaskStatus = () => {
  const dispatch = useDispatch();
  const { status, result, error } = useSelector((state) => state.task);
  const [copied, setCopied] = React.useState({});
  const [showViewModal, setShowViewModal] = React.useState(false);
  const [viewUrl, setViewUrl] = React.useState('');
  const [viewLoading, setViewLoading] = React.useState(false);
  const [pageLoadError, setPageLoadError] = React.useState(null);

  const closeViewModal = () => {
    revokeViewBlobUrl(viewUrl);
    setShowViewModal(false);
    setViewUrl('');
    setPageLoadError(null);
  };

  const handleView = async () => {
    if (!result?.task_id) return;

    setShowViewModal(true);
    setViewLoading(true);
    setPageLoadError(null);
    revokeViewBlobUrl(viewUrl);
    setViewUrl('');

    try {
      const blobUrl = await flaskServiceAPI.fetchRawPageViewUrl(result.task_id);
      setViewUrl(blobUrl);
    } catch (err) {
      console.error('Failed to load page content:', err);
      setPageLoadError(
        getTranslatedError(err, 'Не удалось загрузить содержимое страницы. Возможно, файл поврежден или удален.')
      );
    } finally {
      setViewLoading(false);
    }
  };

  const copyToClipboard = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied({ ...copied, [key]: true });
      setTimeout(() => {
        setCopied({ ...copied, [key]: false });
      }, 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied({ ...copied, [key]: true });
      setTimeout(() => {
        setCopied({ ...copied, [key]: false });
      }, 2000);
    }
  };

  const handleDownload = async (taskId) => {
    try {
      const blob = await flaskServiceAPI.downloadResult(taskId);

      let filename = 'протокол.pdf';
      if (result?.protocol_number) {
        filename = `протокол_${result.protocol_number}.pdf`;
      } else if (result?.task_id) {
        const parts = result.task_id.split('/');
        if (parts.length === 4) {
          const [year, month, day, number] = parts;
          filename = `протокол_${year}${month}${day}${number}.pdf`;
        }
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
      alert('Ошибка при скачивании файла: ' + getTranslatedError(err, 'Неизвестная ошибка'));
    }
  };

  let formattedDate = '—';
  let protocolNumber = '—';
  let fullDownloadUrl = '';
  let fullMhtmlViewUrl = '';

  if (result) {
    if (result.protocol_number) {
      protocolNumber = `№${result.protocol_number}`;
    } else if (result.task_id) {
      const parts = result.task_id.split('/');
      if (parts.length === 4) {
        const [year, month, day, number] = parts;
        protocolNumber = `№${year}${month}${day}${number}`;
      }
    }

    if (result.protocol_date) {
      formattedDate = result.protocol_date;
    } else if (result.task_id) {
      const parts = result.task_id.split('/');
      if (parts.length === 4) {
        const [year, month, day] = parts;
        formattedDate = `${day}.${month}.${year}`;
      }
    }

    if (result.download_url) {
      fullDownloadUrl = result.download_url.startsWith('http')
        ? result.download_url
        : `${window.location.origin}${result.download_url}`;
    }

    if (result.mhtml_view_url) {
      if (result.mhtml_view_url.startsWith('http')) {
        fullMhtmlViewUrl = result.mhtml_view_url;
      } else {
        const url = result.mhtml_view_url.startsWith('/')
          ? result.mhtml_view_url
          : `/${result.mhtml_view_url}`;
        fullMhtmlViewUrl = `${window.location.origin}${url}`;
      }
    }
  }

  if (status === 'idle') return null;

  const mhtmlWarnings = result?.mhtml_warnings || [];
  const mhtmlError = result?.mhtml_error;
  const hasMhtmlIssues = mhtmlWarnings.length > 0 || !!mhtmlError;

  const handleMhtmlUploadSuccess = (taskData) => {
    const taskResult = taskData.result || {};
    dispatch(updateTask({
      status: 'completed',
      result: {
        task_id: taskData.id || result?.task_id,
        download_url: taskResult.download_url,
        mhtml_view_url: taskResult.mhtml_view_url,
        mhtml_download_url: taskResult.mhtml_download_url,
        mhtml_warnings: taskResult.mhtml_warnings || [],
        mhtml_error: taskResult.mhtml_error || null,
        protocol_number: taskData.protocol_number || result?.protocol_number,
        protocol_date: taskData.protocol_date || result?.protocol_date,
        mhtml_replaced_at: taskResult.mhtml_replaced_at,
        mhtml_filename: taskResult.mhtml_filename,
      },
    }));
  };

  const viewPageButton = fullMhtmlViewUrl ? (
    <Button
      variant="outline-primary"
      onClick={handleView}
      style={{
        borderRadius: '8px',
        fontWeight: '500',
        fontSize: '13px',
        padding: '8px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
      }}
    >
      <EyeOutlined /> Просмотреть страницу
    </Button>
  ) : null;

  return (
    <div className="mt-3">
      {status === 'processing' && result?.task_id && status !== 'completed' && (
        <Card className="border-0 shadow-sm" style={{ borderLeft: '4px solid #1890ff !important' }}>
          <Card.Body className="p-3">
            <div className="d-flex align-items-center mb-2">
              <Spinner animation="border" variant="primary" className="me-2" size="sm" />
              <div>
                <Card.Title className="mb-1" style={{ fontSize: '16px' }}>
                  Протокол создается
                </Card.Title>
                <Card.Text className="text-muted mb-0" style={{ fontSize: '13px' }}>
                  Пожалуйста, подождите. Это может занять несколько минут...
                </Card.Text>
              </div>
            </div>

            <div className="mb-3">
              <div className="d-flex align-items-center gap-2 mb-2">
                <Badge bg="success" style={{ fontSize: '12px', padding: '6px 10px' }}>
                  {protocolNumber.replace('№', '')}
                </Badge>
                <span className="text-muted" style={{ fontSize: '13px' }}>от</span>
                <Badge bg="info" style={{ fontSize: '12px', padding: '6px 10px' }}>
                  {formattedDate}
                </Badge>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => copyToClipboard(`Протокол ${protocolNumber.replace('№', '')} от ${formattedDate}`, 'protocol')}
                  title="Копировать в буфер обмена"
                  style={{ marginLeft: 'auto', fontSize: '12px', padding: '4px 8px' }}
                >
                  {copied.protocol ? <CheckCircleOutlined /> : <CopyOutlined />}
                </Button>
              </div>

              {fullDownloadUrl && (
                <div className="mb-2">
                  <Form.Label className="fw-bold mb-1" style={{ fontSize: '13px' }}>Ссылка на протокол:</Form.Label>
                  <InputGroup>
                    <FormControl
                      readOnly
                      value={fullDownloadUrl}
                      onClick={(e) => e.target.select()}
                      style={{
                        borderRadius: '8px 0 0 8px',
                        border: '2px solid #e0e0e0',
                        fontSize: '13px',
                        padding: '8px 12px',
                      }}
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={() => copyToClipboard(fullDownloadUrl, 'download')}
                      style={{ borderRadius: '0 8px 8px 0', fontSize: '12px', padding: '8px 12px' }}
                    >
                      {copied.download ? <CheckCircleOutlined /> : <CopyOutlined />}
                    </Button>
                  </InputGroup>
                </div>
              )}

              {fullMhtmlViewUrl && (
                <div className="mb-2">
                  <Form.Label className="fw-bold mb-1" style={{ fontSize: '13px' }}>Ссылка на просмотр страницы:</Form.Label>
                  <InputGroup>
                    <FormControl
                      readOnly
                      value={fullMhtmlViewUrl}
                      onClick={(e) => e.target.select()}
                      style={{
                        borderRadius: '8px 0 0 8px',
                        border: '2px solid #e0e0e0',
                        fontSize: '13px',
                        padding: '8px 12px',
                      }}
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={() => copyToClipboard(fullMhtmlViewUrl, 'mhtml')}
                      style={{ borderRadius: '0 8px 8px 0', fontSize: '12px', padding: '8px 12px' }}
                    >
                      {copied.mhtml ? <CheckCircleOutlined /> : <CopyOutlined />}
                    </Button>
                  </InputGroup>
                </div>
              )}
            </div>

            {(result.download_url || result.mhtml_view_url) && (
              <div className="d-flex gap-2 flex-wrap">
                {viewPageButton}
                {result.download_url && (
                  <Button
                    variant="success"
                    onClick={() => handleDownload(result.task_id)}
                    style={{
                      borderRadius: '8px',
                      fontWeight: '500',
                      fontSize: '13px',
                      padding: '8px 16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                  >
                    <DownloadOutlined /> Скачать протокол
                  </Button>
                )}
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {status === 'completed' && result && (
        <Card className="border-0 shadow-sm" style={{ borderLeft: '4px solid #52c41a !important' }}>
          <Card.Body className="p-3">
            <div className="d-flex align-items-center mb-2">
              <CheckCircleOutlined style={{ fontSize: '24px', color: '#52c41a', marginRight: '10px' }} />
              <div>
                <Card.Title className="mb-0" style={{ fontSize: '18px', color: '#52c41a' }}>
                  Протокол готов!
                </Card.Title>
                <Card.Text className="text-muted mb-0" style={{ fontSize: '13px' }}>
                  {hasMhtmlIssues
                    ? 'Протокол готов, но при сохранении страницы были предупреждения'
                    : 'Скриншот и протокол успешно сгенерированы'}
                </Card.Text>
              </div>
            </div>

            {hasMhtmlIssues && (
              <Alert variant="warning" className="py-2 px-3 mb-3" style={{ fontSize: '13px' }}>
                {mhtmlError && <div>{mhtmlError}</div>}
                {mhtmlWarnings.map((warning, index) => (
                  <div key={index}>{warning}</div>
                ))}
              </Alert>
            )}

            {result?.task_id && (
              <MhtmlUploadPanel
                taskId={result.task_id}
                hasWarnings={hasMhtmlIssues}
                onSuccess={handleMhtmlUploadSuccess}
                defaultExpanded={hasMhtmlIssues}
              />
            )}

            <div className="mb-3">
              <div className="d-flex align-items-center gap-2 mb-2">
                <Badge bg="success" style={{ fontSize: '12px', padding: '6px 10px' }}>
                  {protocolNumber.replace('№', '')}
                </Badge>
                <span className="text-muted" style={{ fontSize: '13px' }}>от</span>
                <Badge bg="info" style={{ fontSize: '12px', padding: '6px 10px' }}>
                  {formattedDate}
                </Badge>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => copyToClipboard(`Протокол ${protocolNumber.replace('№', '')} от ${formattedDate}`, 'protocol')}
                  title="Копировать в буфер обмена"
                  style={{ marginLeft: 'auto', fontSize: '12px', padding: '4px 8px' }}
                >
                  {copied.protocol ? <CheckCircleOutlined /> : <CopyOutlined />}
                </Button>
              </div>

              <div className="mb-2">
                <Form.Label className="fw-bold mb-1" style={{ fontSize: '13px' }}>Ссылка на протокол:</Form.Label>
                <InputGroup>
                  <FormControl
                    readOnly
                    value={fullDownloadUrl}
                    onClick={(e) => e.target.select()}
                    style={{
                      borderRadius: '8px 0 0 8px',
                      border: '2px solid #e0e0e0',
                      fontSize: '13px',
                      padding: '8px 12px',
                    }}
                  />
                  <Button
                    variant="outline-secondary"
                    onClick={() => copyToClipboard(fullDownloadUrl, 'download')}
                    style={{ borderRadius: '0 8px 8px 0', fontSize: '12px', padding: '8px 12px' }}
                  >
                    {copied.download ? <CheckCircleOutlined /> : <CopyOutlined />}
                  </Button>
                </InputGroup>
              </div>

              {fullMhtmlViewUrl && (
                <div className="mb-2">
                  <Form.Label className="fw-bold mb-1" style={{ fontSize: '13px' }}>Ссылка на просмотр страницы:</Form.Label>
                  <InputGroup>
                    <FormControl
                      readOnly
                      value={fullMhtmlViewUrl}
                      onClick={(e) => e.target.select()}
                      style={{
                        borderRadius: '8px 0 0 8px',
                        border: '2px solid #e0e0e0',
                        fontSize: '13px',
                        padding: '8px 12px',
                      }}
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={() => copyToClipboard(fullMhtmlViewUrl, 'mhtml')}
                      style={{ borderRadius: '0 8px 8px 0', fontSize: '12px', padding: '8px 12px' }}
                    >
                      {copied.mhtml ? <CheckCircleOutlined /> : <CopyOutlined />}
                    </Button>
                  </InputGroup>
                </div>
              )}
            </div>

            <div className="d-flex gap-2 flex-wrap">
              {viewPageButton}
              <Button
                variant="success"
                onClick={() => handleDownload(result.task_id)}
                style={{
                  borderRadius: '8px',
                  fontWeight: '500',
                  fontSize: '13px',
                  padding: '8px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <DownloadOutlined /> Скачать протокол
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}

      {status === 'failed' && error && (
        <Card className="border-0 shadow-sm" style={{ borderLeft: '4px solid #ff4d4f !important' }}>
          <Card.Body className="p-3">
            <div className="d-flex align-items-center">
              <CloseCircleOutlined style={{ fontSize: '24px', color: '#ff4d4f', marginRight: '10px' }} />
              <div>
                <Card.Title className="mb-1" style={{ fontSize: '16px', color: '#ff4d4f' }}>
                  Ошибка!
                </Card.Title>
                <Card.Text className="mb-0" style={{ fontSize: '13px' }}>
                  {error}
                </Card.Text>
              </div>
            </div>
          </Card.Body>
        </Card>
      )}

      <Modal
        show={showViewModal}
        onHide={closeViewModal}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Просмотр страницы</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 0, height: '80vh' }}>
          {viewLoading ? (
            <div className="d-flex justify-content-center align-items-center h-100">
              <Spinner animation="border" variant="primary" />
              <span className="ms-2">Загрузка содержимого страницы...</span>
            </div>
          ) : pageLoadError ? (
            <div className="d-flex flex-column justify-content-center align-items-center h-100 p-4 text-center">
              <CloseCircleOutlined style={{ fontSize: '48px', color: '#ff4d4f', marginBottom: '16px' }} />
              <h5 className="text-danger">Ошибка загрузки страницы</h5>
              <p className="text-muted">{pageLoadError}</p>
            </div>
          ) : viewUrl ? (
            <iframe
              key={viewUrl}
              src={viewUrl}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                minHeight: '600px',
              }}
              title="Просмотр страницы"
              sandbox="allow-same-origin allow-scripts"
            />
          ) : (
            <div className="d-flex justify-content-center align-items-center h-100">
              <span className="text-muted">Нет данных для просмотра</span>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeViewModal}>
            Закрыть
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TaskStatus;
